import { useState } from "react";
import { MARCADORES_POR_FOLHA, TAMANHO_MAXIMO_DO_ESTILO } from "@foto/shared";
import type { SaidaRota } from "../../lib/trpc.js";
import { trpc } from "../../lib/trpc.js";
import { useAuth } from "../auth/useAuth.js";
import { Pagina } from "../../components/Pagina.js";
import { Area, Botao, Campo, Entrada, Falha, Girando, Selo } from "../../components/ui.js";

type Modelo = SaidaRota["modelosDeMarcador"]["list"][number];

/**
 * Os modelos de marcador: as três direções de arte de um evento, prontas para usar
 * na hora de gerar.
 *
 * O do admin vale para todos e aparece com selo; o do cliente é só dele. Só o dono
 * edita o seu — o admin edita qualquer um —, a mesma regra das playlists.
 */
export function ModelosDeMarcadorPage() {
  const { ehAdmin, papel, usuario } = useAuth();
  const modelos = trpc.modelosDeMarcador.list.useQuery();
  const [criando, setCriando] = useState(false);

  const podeCriar = ehAdmin || papel === "cliente";
  const podeEditar = (m: Modelo) =>
    ehAdmin || (papel === "cliente" && m.ownerUserId === usuario?.id);

  return (
    <Pagina
      titulo="Modelos de marcador"
      descricao="As três direções de arte de um evento, prontas para usar. Os do sistema valem para todos."
      acao={
        podeCriar && !criando ? (
          <Botao tom="brasa" onClick={() => setCriando(true)}>
            Novo modelo
          </Botao>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3">
        {criando && (
          <div className="rounded-peca border border-borda bg-superficie p-4">
            <FormularioDoModelo aoTerminar={() => setCriando(false)} doSistema={ehAdmin} />
          </div>
        )}

        {modelos.isLoading ? (
          <Girando className="mx-auto my-6 size-5 text-texto-fraco" />
        ) : modelos.isError ? (
          <Falha mensagem={modelos.error.message} tentarNovamente={() => void modelos.refetch()} />
        ) : (modelos.data ?? []).length === 0 && !criando ? (
          <p className="text-[0.875rem] text-texto-suave">Nenhum modelo ainda.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {(modelos.data ?? []).map((m) => (
              <li key={m.id}>
                <CartaoDoModelo modelo={m} editavel={podeEditar(m)} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Pagina>
  );
}

function CartaoDoModelo({ modelo, editavel }: { modelo: Modelo; editavel: boolean }) {
  const utils = trpc.useUtils();
  const [editando, setEditando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const apagar = trpc.modelosDeMarcador.delete.useMutation({
    onSuccess: () => utils.modelosDeMarcador.list.invalidate(),
  });

  if (editando) {
    return (
      <div className="rounded-peca border border-borda bg-superficie p-4">
        <FormularioDoModelo
          modelo={modelo}
          doSistema={modelo.doSistema}
          aoTerminar={() => setEditando(false)}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-peca border border-borda bg-superficie px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 text-[0.9375rem] font-medium text-texto">{modelo.nome}</p>
        {modelo.doSistema && <Selo tom="projecao">Do sistema</Selo>}
      </div>
      {modelo.descricao && (
        <p className="text-[0.8125rem] leading-relaxed text-texto-suave">{modelo.descricao}</p>
      )}

      <details>
        <summary className="min-h-9 cursor-pointer text-[0.8125rem] text-texto-suave">
          Ver as três direções
        </summary>
        <ol className="mt-2 flex list-decimal flex-col gap-2 pl-5 text-[0.8125rem] leading-relaxed text-texto-suave">
          {modelo.estilos.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ol>
      </details>

      {editavel && (
        <div className="flex flex-wrap items-center gap-3">
          <Botao tom="fantasma" formato="nu" onClick={() => setEditando(true)}>
            Editar
          </Botao>
          {confirmando ? (
            <>
              <span className="text-[0.8125rem] text-texto-suave">
                Apagar? Os marcadores já feitos continuam.
              </span>
              <Botao
                tom="descartar"
                carregando={apagar.isPending}
                onClick={() => apagar.mutate({ id: modelo.id })}
              >
                Apagar
              </Botao>
              <Botao tom="quieto" onClick={() => setConfirmando(false)}>
                Não apagar
              </Botao>
            </>
          ) : (
            <Botao tom="fantasma" formato="nu" onClick={() => setConfirmando(true)}>
              Apagar
            </Botao>
          )}
        </div>
      )}
      {apagar.isError && <Falha mensagem={apagar.error.message} />}
    </div>
  );
}

/** Cria um modelo, ou edita o que veio em `modelo`. */
function FormularioDoModelo({
  modelo,
  doSistema,
  aoTerminar,
}: {
  modelo?: Modelo;
  doSistema: boolean;
  aoTerminar: () => void;
}) {
  const utils = trpc.useUtils();
  const [nome, setNome] = useState(modelo?.nome ?? "");
  const [descricao, setDescricao] = useState(modelo?.descricao ?? "");
  const [estilos, setEstilos] = useState<string[]>(
    modelo?.estilos ?? Array.from({ length: MARCADORES_POR_FOLHA }, () => ""),
  );
  const [titulo, setTitulo] = useState(modelo?.camposPadrao.titulo ?? "");
  const [subtitulo, setSubtitulo] = useState(modelo?.camposPadrao.subtitulo ?? "");
  const [frase, setFrase] = useState(modelo?.camposPadrao.frase ?? "");
  const [rodape, setRodape] = useState(modelo?.camposPadrao.rodape ?? "");
  const [observacao, setObservacao] = useState(modelo?.observacao ?? "");

  const aoSalvar = async () => {
    await utils.modelosDeMarcador.list.invalidate();
    aoTerminar();
  };
  const criar = trpc.modelosDeMarcador.create.useMutation({ onSuccess: aoSalvar });
  const atualizar = trpc.modelosDeMarcador.update.useMutation({ onSuccess: aoSalvar });
  const ocupado = criar.isPending || atualizar.isPending;
  const erro = criar.error ?? atualizar.error;

  const valido = nome.trim().length >= 2 && estilos.every((e) => e.trim().length >= 10);

  function salvar() {
    // Campo vazio não vira sugestão: sai do objeto.
    const camposPadrao = Object.fromEntries(
      Object.entries({ titulo, subtitulo, frase, rodape }).filter(([, v]) => v.trim() !== ""),
    );
    const corpo = { nome, descricao, estilos, camposPadrao, observacao };
    if (modelo) atualizar.mutate({ id: modelo.id, ...corpo });
    else criar.mutate(corpo);
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (valido) salvar();
      }}
    >
      <h2 className="font-display text-[1.125rem] leading-tight">
        {modelo ? "Editar modelo" : doSistema ? "Novo modelo do sistema" : "Novo modelo"}
      </h2>

      <Campo rotulo="Nome">
        <Entrada
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          maxLength={60}
          autoFocus
          placeholder="Páscoa"
        />
      </Campo>
      <Campo rotulo="Descrição (opcional)">
        <Entrada value={descricao} onChange={(e) => setDescricao(e.target.value)} maxLength={200} />
      </Campo>

      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 text-[0.8125rem] font-medium text-texto-suave">
          As três direções de arte, uma por marcador
        </legend>
        {estilos.map((e, i) => (
          <Campo
            key={i}
            rotulo={`Marcador ${i + 1}`}
            dica={
              i === 0
                ? "Ambientação, cores, tipografia e enfeites. Ex.: amanhecer dourado, céu azul-claro, título em serifa azul-marinho."
                : undefined
            }
          >
            <Area
              value={e}
              onChange={(ev) => setEstilos(estilos.map((x, j) => (j === i ? ev.target.value : x)))}
              maxLength={TAMANHO_MAXIMO_DO_ESTILO}
              className="min-h-[5rem]"
            />
          </Campo>
        ))}
      </fieldset>

      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 text-[0.8125rem] font-medium text-texto-suave">
          Textos de partida (opcional)
        </legend>
        <p className="text-[0.8125rem] text-texto-fraco">
          Preenchem os campos de quem escolhe o modelo, que pode mudá-los.
        </p>
        <Campo rotulo="Título">
          <Entrada value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={50} />
        </Campo>
        <Campo rotulo="Subtítulo">
          <Entrada
            value={subtitulo}
            onChange={(e) => setSubtitulo(e.target.value)}
            maxLength={60}
          />
        </Campo>
        <Campo rotulo="Frase">
          <Entrada value={frase} onChange={(e) => setFrase(e.target.value)} maxLength={80} />
        </Campo>
        <Campo rotulo="Rodapé">
          <Area
            value={rodape}
            onChange={(e) => setRodape(e.target.value)}
            maxLength={100}
            rows={2}
            className="min-h-[4.5rem]"
          />
        </Campo>
        <Campo rotulo="Observação para a IA">
          <Entrada
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            maxLength={300}
          />
        </Campo>
      </fieldset>

      {erro && <Falha mensagem={erro.message} />}

      <div className="flex flex-wrap justify-end gap-2 border-t border-borda pt-4">
        <Botao tom="quieto" type="button" onClick={aoTerminar} disabled={ocupado}>
          Cancelar
        </Botao>
        <Botao tom="brasa" type="submit" carregando={ocupado} disabled={!valido}>
          Salvar
        </Botao>
      </div>
    </form>
  );
}
